import {
  Text,
  Stack,
  Button,
  Group,
  Box,
  Menu,
  Portal,
  useMantineTheme,
} from '@mantine/core';
import { RowLayout } from '../config-row';
import { SectionLayout } from '@/common';
import { Add, Edit, Options, Reset } from '@morph-mapper/ui';
import { useEntryContext } from '../../context';
import { useNavigate } from 'react-router';
import {
  LogicBlockIndex,
  useLogicBlocks,
  useStructureBlocks,
} from '@morph-mapper/node-logic';
import { useStore } from '@/store';
import { IoMdRadioButtonOff, IoMdRadioButtonOn } from 'react-icons/io';
import { EntryType, ReservedNodeType } from '@morph-mapper/types';
import { AiSuggestions } from '../../../common/features/variable-row/molecules/ai-suggestions';
import { GraphRefType } from '../../../graph-editor/types/graph';
import { buildIteratorGraphPreset } from '../../../graph-editor/lib/presets/iterator';
import { t } from 'i18next';
import { DirtyType } from '@/types';
import { useState } from 'react';

export const IteratorPanel = () => {
  const variant = useStore(({ config: c }) => c.getVariant());
  const [initGraph, createItem, linkGraph, linkChild, addDirtyAll] = useStore(
    ({ graphs: g, entries: e }) => [
      g.init,
      e.createItem,
      e.linkGraph,
      e.linkChild,
      e.addDirtyAll,
    ],
  );
  const [
    hasIterator,
    entryId,
    iteratorId,
    outputType,
    onDeleteIterator,
    iteratorType,
    setIteratorType,
  ] = useEntryContext((s) => [
    s.hasIterator(),
    s.getId(),
    s.getIteratorId(),
    s.getOutputType(),
    s.deleteIterator,
    s.getIteratorType(),
    s.setIteratorType,
  ]);
  const navigate = useNavigate();
  const { structureBlocks } = useStructureBlocks(variant);
  const { logicBlocks, logicBlocksByCategory } = useLogicBlocks(variant);
  const theme = useMantineTheme();
  const [pendingIteratorType, setPendingIteratorType] = useState<EntryType>(
    EntryType.Graph,
  );

  const handleChangeMapType = () => {
    navigate('structure');
  };
  const handleEditIterator = () => {
    if (iteratorType === EntryType.Code) {
      navigate(`../../${iteratorId}/code`);
    } else {
      navigate(`../../${iteratorId}/graph`);
    }
  };
  const handleCreateCodeIterator = () => {
    const iteratorEntryId = createItem(
      { key: ReservedNodeType.Iterator, parentId: entryId },
      EntryType.Code,
    );

    linkChild(entryId, iteratorEntryId);
    addDirtyAll(DirtyType.Underlying);
    navigate(`../../${iteratorEntryId}/code`);
  };

  const handleCreateIterator = (type: LogicBlockIndex) => {
    const iteratorKey = ReservedNodeType.Iterator;

    const iteratorEntryId = createItem(
      { key: iteratorKey, parentId: entryId },
      EntryType.Graph,
    );

    const { nodes, edges } = buildIteratorGraphPreset(type, logicBlocks);
    const graphId = initGraph({
      ref: {
        kind: GraphRefType.Field,
        fieldId: iteratorEntryId,
      },
      nodes,
      edges,
    });

    linkGraph(iteratorEntryId, graphId);
    linkChild(entryId, iteratorEntryId);
    addDirtyAll(DirtyType.Underlying);
    navigate(`../../${iteratorEntryId}/graph`);
  };

  const handleTypeChange = (isCode: boolean) => {
    setIteratorType(isCode ? EntryType.Code : EntryType.Graph);
  };

  return (
    <Stack spacing={'xl'}>
      <Text c="dimmed">
        Iterators are used to extract values which are represented in a
        repeating structure. Iterators can be referenced in the graph of nested
        variables.
      </Text>
      <SectionLayout title="General" description="">
        <RowLayout
          title="Map Type"
          description="Configure the current map type."
        >
          <Button onClick={handleChangeMapType} variant="default">
            {structureBlocks[outputType].title}
          </Button>
        </RowLayout>
        <RowLayout
          title={
            'Iterator' +
            ((iteratorType || pendingIteratorType) === EntryType.Code
              ? ' (Code)'
              : ' (Graph)')
          }
          description="Iterator definition and configuration"
        >
          {hasIterator ? (
            <Group>
              <Edit onClick={handleEditIterator} />
              <Reset onClick={onDeleteIterator} />
              <AiSuggestions iterator={true} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Menu shadow="md" width={150} position="bottom-end">
                  <Menu.Target>
                    <Options />
                  </Menu.Target>
                  <Portal>
                    <Menu.Dropdown sx={{ zIndex: 1000 }}>
                      <Menu.Label>Type</Menu.Label>
                      <Menu.Item
                        icon={
                          iteratorType !== EntryType.Code ? (
                            <IoMdRadioButtonOn color={theme.colors.blue[7]} />
                          ) : (
                            <IoMdRadioButtonOff color={theme.colors.gray[6]} />
                          )
                        }
                        onClick={() => handleTypeChange(false)}
                      >
                        Graph
                      </Menu.Item>
                      <Menu.Item
                        icon={
                          iteratorType === EntryType.Code ? (
                            <IoMdRadioButtonOn color={theme.colors.blue[7]} />
                          ) : (
                            <IoMdRadioButtonOff color={theme.colors.gray[6]} />
                          )
                        }
                        onClick={() => handleTypeChange(true)}
                      >
                        Code
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Portal>
                </Menu>
              </Box>
            </Group>
          ) : (
            <Group>
              {pendingIteratorType === EntryType.Code ? (
                <Edit onClick={handleCreateCodeIterator} />
              ) : (
                <Menu shadow="md" width={260} position="bottom-start">
                  <Menu.Target>
                    <Add />
                  </Menu.Target>
                  <Portal>
                    <Menu.Dropdown sx={{ zIndex: 1000 }}>
                      <Menu.Label>Iterator type</Menu.Label>
                      {logicBlocksByCategory['iterator'].map(({ type }) => (
                        <Menu.Item
                          key={type}
                          onClick={() => handleCreateIterator(type)}
                        >
                          {t(logicBlocks[type].title)}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Portal>
                </Menu>
              )}
              <AiSuggestions iterator={true} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Menu shadow="md" width={150} position="bottom-end">
                  <Menu.Target>
                    <Options />
                  </Menu.Target>
                  <Portal>
                    <Menu.Dropdown sx={{ zIndex: 1000 }}>
                      <Menu.Label>Type</Menu.Label>
                      <Menu.Item
                        icon={
                          pendingIteratorType !== EntryType.Code ? (
                            <IoMdRadioButtonOn color={theme.colors.blue[7]} />
                          ) : (
                            <IoMdRadioButtonOff color={theme.colors.gray[6]} />
                          )
                        }
                        onClick={() => setPendingIteratorType(EntryType.Graph)}
                      >
                        Graph
                      </Menu.Item>
                      <Menu.Item
                        icon={
                          pendingIteratorType === EntryType.Code ? (
                            <IoMdRadioButtonOn color={theme.colors.blue[7]} />
                          ) : (
                            <IoMdRadioButtonOff color={theme.colors.gray[6]} />
                          )
                        }
                        onClick={() => setPendingIteratorType(EntryType.Code)}
                      >
                        Code
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Portal>
                </Menu>
              </Box>
            </Group>
          )}
        </RowLayout>
      </SectionLayout>
    </Stack>
  );
};
